import { useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/features/auth/authSlice';
import {
  selectActiveRoleForCurrentUser,
  selectActiveMembershipForCurrentUser,
  selectActiveOrganization,
  selectCurrentUserProfile,
} from '@/store/selectors/authorizationSelectors';
import { AuthUserTypeName } from '@/domain/entities/auth';
import { normalizeRoleKey } from '@/domain/entities/role';
import { getFixedOrgRoleAccess } from '@/lib/fixedRoles';
import { getAllPermissions, type PermissionValue } from '@/lib/permissions';

export interface PermissionAPI {
  ready: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (roleName: string) => boolean;
  isSuperAdmin: boolean;
  isSystemUser: boolean;
  isBusinessUser: boolean;
  permissions: string[];
  roleName: string | null;
  userType: AuthUserTypeName | null;
}

function toUnique(list: string[]) {
  return Array.from(new Set(list));
}

export function usePermissions(): PermissionAPI {
  const profile = useAppSelector(selectCurrentUserProfile);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const userAuthType = useAppSelector((state) => state.auth.authUserType) || null;
  const activeMembership = useAppSelector(selectActiveMembershipForCurrentUser);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const activeRole = useAppSelector(selectActiveRoleForCurrentUser);

  return useMemo(() => {
    const membershipRoleKey = normalizeRoleKey(activeMembership?.roleKey);

    const orgCapabilities = toUnique(activeOrganization?.capabilities ?? []);
    const orgCapabilitiesSet = new Set(orgCapabilities);
    const rolePermissions = toUnique(activeRole?.permissions ?? []);
    const fixedRoleAccess = getFixedOrgRoleAccess(membershipRoleKey, orgCapabilities);
    const roleName = fixedRoleAccess?.roleName ?? activeRole?.name ?? null;

    const effectivePermissions = isSuperAdmin
      ? getAllPermissions()
      : fixedRoleAccess
        ? fixedRoleAccess.permissions
        : rolePermissions.filter((permission) => orgCapabilitiesSet.has(permission));

    const effectivePermissionSet = new Set(effectivePermissions);
    const ready =
      isSuperAdmin ||
      Boolean(profile?.applicationUserId && activeMembership && (!membershipRoleKey || fixedRoleAccess || activeRole));

    return {
      ready,
      hasPermission: (permission: string) => isSuperAdmin || effectivePermissionSet.has(permission),
      hasAnyPermission: (permissions: string[]) =>
        isSuperAdmin || permissions.some((permission) => effectivePermissionSet.has(permission)),
      hasAllPermissions: (permissions: string[]) =>
        isSuperAdmin || permissions.every((permission) => effectivePermissionSet.has(permission)),
      hasRole: (name: string) => isSuperAdmin || roleName === name || membershipRoleKey === normalizeRoleKey(name),
      isSuperAdmin: isSuperAdmin, //Only Super admin relies on Cognito role
      isSystemUser: userAuthType === AuthUserTypeName.SystemUser || isSuperAdmin,
      isBusinessUser: userAuthType === AuthUserTypeName.BusinessUser,
      permissions: effectivePermissions as PermissionValue[],
      roleName,
      userType: userAuthType,
    };
  }, [activeMembership, activeOrganization, activeRole, profile?.applicationUserId, isSuperAdmin, userAuthType]);
}

// Convenience hook for single permission check
export function useHasPermission(permission: string): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
}
